import {useState, useEffect, useRef} from 'react';

const SEARCH_HINTS = [
    'Search contacts, companies, leads…',
    'Try "Acme Corp"',
    'Try "John Smith"',
    'Search by name, email, or phone…',
];

const COMMAND_HINTS = [
    'Type a command or search…',
    'Try "go to dashboard"',
    'Try "create new lead"',
    'Try "generate report for Q4 pipeline"',
    'Try "open keyboard shortcuts"',
    'Try "schedule a meeting"',
];

const ROTATION_INTERVAL_MS = 4000;

export type PlaceholderState = {
    current: string;
    previous: string | null;
    /** Increments on every transition — use as a React key to trigger animations */
    generation: number;
};

/**
 * Rotates through placeholder hints with transition state for animations.
 * Returns both current and previous hints so the consumer can cross-fade them.
 */
export function usePlaceholderHints(
    isSearchMode: boolean,
    isOpen: boolean,
    hasValue: boolean
): PlaceholderState {
    const hints = isSearchMode ? SEARCH_HINTS : COMMAND_HINTS;
    const [index, setIndex] = useState(0);
    const [generation, setGeneration] = useState(0);
    const previousRef = useRef<string | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        previousRef.current = null;
        setIndex(0);
        setGeneration(0);
    }, [isSearchMode]);

    useEffect(() => {
        if (!isOpen || hasValue) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            return;
        }

        intervalRef.current = setInterval(() => {
            setIndex((prev) => {
                previousRef.current = hints[prev % hints.length] ?? null;

                return (prev + 1) % hints.length;
            });
            setGeneration((g) => g + 1);
        }, ROTATION_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isOpen, hasValue, hints]);

    return {
        current: hints[index % hints.length] ?? '',
        previous: previousRef.current,
        generation,
    };
}
